import { INITIAL_FILTER_ORDER_WORKLIST } from '#pages/order-worklist';
import { CaretDownOutlined, CheckOutlined, SearchOutlined } from '@vindr/icons';
import { Button, Row, Select } from 'antd';
import { memo, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';
import { FILTER_TYPE } from './constants';
import useSearchOptions from './useSearchOptions';
import { ParamsGetOrders } from '@vindr/services/apis/order.api';
import { extendDateOptions } from '@vindr/ui/src/components/ui/DateRangePicker';

const { Option } = Select;

function Filter({
  initFilters,
  onChange,
  className,
  forceSearch,
}: {
  initFilters: typeof INITIAL_FILTER_ORDER_WORKLIST;
  onChange: (params: any, type?: string) => void;
  className?: string;
  forceSearch: (params: any) => void;
}) {
  const { t } = useTranslation('Vindoc');
  const [params, setParams] = useState<ParamsGetOrders>(initFilters);

  useEffect(() => {
    //? set default institution
    setParams(initFilters);
    onChange(initFilters);
  }, [JSON.stringify(initFilters)]);

  const handleOnChangeFilter = (p, type, values, triggerSearch = false) => {
    if (!type) return;
    let tempFilterData = {};

    switch (type) {
      case FILTER_TYPE.SCHEDULED_DATE:
        tempFilterData = {
          ...p,
          scheduled_date_from: values[0].toISOString(),
          scheduled_date_to: values[1].toISOString(),
        };
        break;
      case FILTER_TYPE.RECEIVED_AT:
        tempFilterData = {
          ...p,
          patient_arrived_date_from: values[0].toISOString(),
          patient_arrived_date_to: values[1].toISOString(),
        };
        break;
      default:
        tempFilterData = {
          ...p,
          [type]: values,
        };
        break;
    }
    setIsFiltered(true);
    if (triggerSearch) {
      onSearch(tempFilterData, type);
    } else {
      onChangeParams(tempFilterData);
    }
  };

  const SEARCH_OPTIONS = useSearchOptions(params, handleOnChangeFilter);

  // [0: Scheduled Date, 1: Status, 2: Modalities, 3: Patient Name, 4: Patient ID,
  // 5: Institution, 6: Visit ID, 7: Order Group 8: Study Description]
  const INITIAL_SEARCH_FIELDS = [0, 1, 2, 3, 4, 5];

  const [searchFields, setSearchFields] = useState([...INITIAL_SEARCH_FIELDS]);
  const [isFiltered, setIsFiltered] = useState(false);

  const onChangeParams = useCallback((p) => {
    setParams(p);
  }, []);

  const onSearch = useCallback((p, type) => {
    setParams(p);
    onChange(p, type);
  }, []);

  // const handleOnSearch = () => {
  //   setIsFiltered(true);
  //   onSearch({ ...params });
  // };

  const handleChangeSearchKey = (key, replaceKey) => {
    const tempParams = { ...params };

    const deleteDateRangeKeys = (fromKey, toKey) => {
      delete tempParams[fromKey];
      delete tempParams[toKey];
    };

    const addDateRangeKeys = (fromKey, toKey) => {
      tempParams[fromKey] = extendDateOptions['Today'][0].toISOString();
      tempParams[toKey] = extendDateOptions['Today'][1].toISOString();
    };

    if (key === FILTER_TYPE.SCHEDULED_DATE) {
      deleteDateRangeKeys('scheduled_date_from', 'scheduled_date_to');
    } else if (key === FILTER_TYPE.RECEIVED_AT) {
      deleteDateRangeKeys('patient_arrived_date_from', 'patient_arrived_date_to');
    } else {
      delete tempParams[key];
    }

    if (replaceKey === FILTER_TYPE.SCHEDULED_DATE) {
      addDateRangeKeys('scheduled_date_from', 'scheduled_date_to');
    } else if (replaceKey === FILTER_TYPE.RECEIVED_AT) {
      addDateRangeKeys('patient_arrived_date_from', 'patient_arrived_date_to');
    }

    setIsFiltered(true);
    onChangeParams(tempParams);

    let tempSearchField = [...searchFields];
    const currentItemIndex = SEARCH_OPTIONS.findIndex((it) => it.key === key);
    const replaceItemIndex = SEARCH_OPTIONS.findIndex((it) => it.key === replaceKey);

    tempSearchField = tempSearchField.map((it) =>
      it === currentItemIndex ? replaceItemIndex : it
    );
    setSearchFields(tempSearchField);
  };

  const handleClearAllFilters = useCallback(() => {
    setSearchFields(INITIAL_SEARCH_FIELDS);
    setParams({ ...initFilters });
    onChange({ ...initFilters });
    setIsFiltered(false);
  }, [initFilters]);

  return (
    <Row className={twMerge('ml-[-7px] space-y-2 pt-3 pb-6', className)} align="bottom">
      <div>
        <Row className="items-start space-y-2 max-sm:flex-col sm:items-end" align="bottom">
          {searchFields &&
            SEARCH_OPTIONS &&
            searchFields.map((idx) => {
              const field = SEARCH_OPTIONS[idx];
              const options = SEARCH_OPTIONS.filter(
                (_, i) => i == idx || !searchFields.includes(i)
              );
              const Component = field?.component;

              return (
                <div key={field?.key} className="search-item">
                  <Row>
                    <div>
                      <Select
                        size="small"
                        className="select-dropdown-light border-0"
                        popupClassName="min-w-40"
                        menuItemSelectedIcon={<CheckOutlined />}
                        suffixIcon={<CaretDownOutlined />}
                        value={field.key}
                        onChange={(value) => handleChangeSearchKey(field.key, value)}
                      >
                        {options.map((item, idx) => (
                          <Option key={idx} value={item.key}>
                            {t(item.text)}
                          </Option>
                        ))}
                      </Select>
                    </div>
                  </Row>
                  <Row className="pl-[7px]">
                    <div>{Component ? Component : null}</div>
                  </Row>
                </div>
              );
            })}
          <Row className="ml-[7px] flex items-end">
            <div className="mr-[7px]">
              <Button
                type="primary"
                className="flex items-center"
                size="small"
                // type="primary"
                icon={<SearchOutlined />}
                onClick={() => forceSearch?.(params)}
              >
                {t('Search')}
              </Button>
            </div>
            <div className="mr-0">
              <Button
                className="filter-btn"
                size="small"
                disabled={!isFiltered}
                // icon={<ReloadOutlined />}
                onClick={() => handleClearAllFilters()}
              >
                {t('Clear Filter')}
              </Button>
            </div>
          </Row>
        </Row>
      </div>
    </Row>
  );
}

export default memo(Filter);
